<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useGeolocationStore, type NearbyExplosion, type NearbyTrajectory } from '@/modules/geolocation';
import { useDetectionStore } from '@/modules/detection';
import DebugLogPanel from './DebugLogPanel.vue';
import RadarMapView from './RadarMapView.vue';
import SystemStatusBar from './SystemStatusBar.vue';
import RadarToolsPanel from './RadarToolsPanel.vue';
import EventFeed from './EventFeed.vue';
import MyEventsFeed from './MyEventsFeed.vue';
import OnboardingTour from './OnboardingTour.vue';
import { useRadarLogic } from './useRadarLogic';
import { useOnboarding } from './useOnboarding';

/**
 * Layout-shell радара: собирает компоненты и связывает их с useRadarLogic.
 * Логика (локация/сокет/мониторинг) — в composable; Leaflet — в RadarMapView.
 */

const mapView = ref<InstanceType<typeof RadarMapView> | null>(null);

/**
 * Ref to the My-Events feed so we can report the outcome of an audio upload back to the card
 * (it exposes `markAudioSent` / `markAudioSendError` via defineExpose). The upload itself is
 * delegated to useLiveDetection.uploadAudioSample.
 */
const myEvents = ref<InstanceType<typeof MyEventsFeed> | null>(null);

/**
 * «Отправить для анализа» handler: uploads the WAV clip + the full detection reasoning via the
 * detection composable, then reports the result back to the card's button state. The composable
 * throws on failure, which we surface as an error message; on success it returns the sample id.
 */
async function onSendAudio(
  payload: { id: number; blob: Blob; report: unknown },
): Promise<void> {
  try {
    await detection.uploadAudioSample(payload.id, payload.blob, payload.report);
    myEvents.value?.markAudioSent(payload.id);
  } catch (e) {
    myEvents.value?.markAudioSendError(
      payload.id,
      e instanceof Error ? e.message : 'Ошибка отправки аудио',
    );
  }
}

// Event feed has two tabs: the user's own detections (detectionStore) vs nearby events
// received from the server (geolocationStore). Counts feed the tab badges.
const geoStore = useGeolocationStore();
const detectionStore = useDetectionStore();
const eventTab = ref<'mine' | 'server'>('server');

/**
 * Resizable right column (side-col) on desktop. The user drags a thin handle between the map and
 * the data column to widen/narrow it. The chosen width persists in localStorage so it survives
 * reloads. Only applies on desktop (≥900px) — on mobile the columns stack vertically and there's
 * no horizontal split. The fallback flex ratio (1 1 0) is used until a width is set / below 900px.
 */
const SIDE_COL_WIDTH_KEY = 'radar.sideColWidth';
const MIN_SIDE_COL_W = 300;
const MAX_SIDE_COL_W = 720;
const sideColWidth = ref<number | null>(null);
const dragging = ref(false);
const bodyRef = ref<HTMLDivElement | null>(null);

const sideColStyle = computed(() => {
  if (sideColWidth.value == null) return {};
  // Clamp again at render time in case the viewport shrank since the value was saved.
  const w = Math.min(Math.max(sideColWidth.value, MIN_SIDE_COL_W), MAX_SIDE_COL_W);
  return { flex: `0 0 ${w}px`, maxWidth: 'none' };
});

function loadSideColWidth(): void {
  try {
    const raw = localStorage.getItem(SIDE_COL_WIDTH_KEY);
    if (raw == null) return;
    const w = Number(raw);
    if (Number.isFinite(w) && w >= MIN_SIDE_COL_W && w <= MAX_SIDE_COL_W) {
      sideColWidth.value = w;
    }
  } catch {
    /* localStorage unavailable — fall back to the flex default */
  }
}

function onSplitterPointerDown(e: PointerEvent): void {
  // Only meaningful in the horizontal (desktop) layout; on mobile the body is a column and there's
  // no splitter rendered (see template + CSS).
  dragging.value = true;
  (e.target as Element).setPointerCapture(e.pointerId);
  document.body.style.userSelect = 'none';
  document.body.style.cursor = 'col-resize';
}

function onSplitterPointerMove(e: PointerEvent): void {
  if (!dragging.value || !bodyRef.value) return;
  // Width = distance from the body's right edge to the pointer.
  const rect = bodyRef.value.getBoundingClientRect();
  const w = rect.right - e.clientX;
  sideColWidth.value = Math.min(Math.max(w, MIN_SIDE_COL_W), MAX_SIDE_COL_W);
}

function onSplitterPointerUp(e: PointerEvent): void {
  if (!dragging.value) return;
  dragging.value = false;
  try {
    (e.target as Element).releasePointerCapture(e.pointerId);
  } catch {
    /* target may have been removed */
  }
  document.body.style.userSelect = '';
  document.body.style.cursor = '';
  if (sideColWidth.value != null) {
    try {
      localStorage.setItem(SIDE_COL_WIDTH_KEY, String(sideColWidth.value));
    } catch {
      /* ignore quota / disabled storage */
    }
  }
}

onMounted(loadSideColWidth);

const {
  detection,
  locating,
  gpsError,
  syncing,
  accuracy,
  locationLabel,
  locationSource,
  manualMode,
  manualLat,
  manualLon,
  manualError,
  manualApplied,
  liveTracking,
  waitingForAlert,
  showMap,
  showQuadrants,
  showTools,
  syncIntervalSec,
  toggleMonitoring,
  manualSync,
  setSyncInterval,
  toggleMap,
  toggleQuadrants,
  setManualLocation,
  retryLocation,
  toggleLiveTracking,
  locateOnce,
  toggleManualMode,
  applyManualCoords,
} = useRadarLogic({
  init: (c, markUser) => mapView.value?.init(c, markUser),
  setCenter: (c, keepZoom) => mapView.value?.setCenter(c, keepZoom),
  updateMarkers: () => mapView.value?.updateMarkers(),
  updateQuadrants: () => mapView.value?.updateQuadrants(),
  updateTrajectories: () => mapView.value?.updateTrajectories(),
  updateTracks: () => mapView.value?.updateTracks(),
  highlightExplosionQuadrant: (row, col, count) => mapView.value?.highlightExplosionQuadrant(row, col, count),
  zoomForPick: (c) => mapView.value?.zoomForPick(c),
});

function focusEvent(e: NearbyExplosion): void {
  mapView.value?.focusEvent(e);
}

function focusTrajectory(t: NearbyTrajectory): void {
  mapView.value?.focusTrajectory(t);
}

// First-run guided tour. Shown automatically on first visit (localStorage flag), and reopenable
// from the ❓ button in the header. Steps highlight elements via data-onboarding-target attributes.
const {
  steps: onboardingSteps,
  stepIndex: onboardingStep,
  visible: onboardingVisible,
  totalSteps: onboardingTotal,
  next: onboardingNext,
  back: onboardingBack,
  skip: onboardingSkip,
  restart: onboardingRestart,
} = useOnboarding();
</script>

<template>
  <div class="live-radar">
    <!-- Header summary + badges -->
    <SystemStatusBar
      :detection="detection"
      :locating="locating"
      :waiting-for-alert="waitingForAlert"
      :accuracy="accuracy"
      :location-label="locationLabel"
      :syncing="syncing"
      :show-map="showMap"
      :show-quadrants="showQuadrants"
      :show-tools="showTools"
      @toggle-monitoring="toggleMonitoring"
      @sync="manualSync"
      @toggle-map="toggleMap"
      @toggle-tools="showTools = !showTools"
      @toggle-quadrants="toggleQuadrants"
      @error="gpsError = $event"
      @restart-onboarding="onboardingRestart"
    />

    <!-- Monitoring errors (the control itself lives in the header) -->
    <div
      v-if="detection.micError.value || detection.initError.value"
      class="monitor-error"
    >
      ⚠ {{ detection.micError.value || detection.initError.value }}
    </div>

    <!-- Body: stacks on phones, splits into map (left) + data/settings (right) on desktop -->
    <div
      ref="bodyRef"
      class="body"
    >
      <RadarMapView
        ref="mapView"
        :show-map="showMap"
        :show-quadrants="showQuadrants"
        :manual-mode="manualMode"
        :accuracy="accuracy"
        :gps-error="gpsError"
        @pick="setManualLocation"
        @retry-location="retryLocation"
      />

      <!-- Drag handle between the map and the data column. Only rendered on desktop (≥900px) via
           CSS; on mobile the body is a vertical stack, so there's nothing to drag horizontally. -->
      <div
        class="splitter"
        :class="{ dragging: dragging }"
        @pointerdown="onSplitterPointerDown"
        @pointermove="onSplitterPointerMove"
        @pointerup="onSplitterPointerUp"
        @pointercancel="onSplitterPointerUp"
        @dblclick="sideColWidth = null"
      />

      <!-- Right column: settings/diagnostics + event feed -->
      <div
        class="side-col"
        :class="{ picking: manualMode }"
        :style="sideColStyle"
      >
        <!-- Settings & diagnostics: toggled from the ⚙ button in the header, collapsed by default -->
        <RadarToolsPanel
          v-if="showTools"
          v-model:manual-lat="manualLat"
          v-model:manual-lon="manualLon"
          :detection="detection"
          :live-tracking="liveTracking"
          :manual-mode="manualMode"
          :location-source="locationSource"
          :manual-error="manualError"
          :manual-applied="manualApplied"
          :sync-interval-sec="syncIntervalSec"
          @toggle-live-tracking="toggleLiveTracking"
          @locate-once="locateOnce"
          @toggle-manual-mode="toggleManualMode"
          @apply-manual-coords="applyManualCoords"
          @set-sync-interval="setSyncInterval"
        />

        <!-- Event feed: hidden on mobile while the settings panel is open or manual placement is
             armed, so the map stays visible (see .feed-collapsed media rule below). -->
        <div
          class="feed-section"
          :class="{ 'feed-collapsed': showTools || manualMode }"
        >
          <!-- Event feed tabs: own detections vs server-side nearby events -->
          <div
            class="feed-tabs"
            data-onboarding-target="feed-tabs"
          >
            <button
              class="feed-tab"
              :class="{ active: eventTab === 'server' }"
              @click="eventTab = 'server'"
            >
              🌐 Рядом
              <span
                v-if="geoStore.nearbyCount + geoStore.nearbyTrajectories.length > 0"
                class="tab-badge"
              >{{ geoStore.nearbyCount + geoStore.nearbyTrajectories.length }}</span>
            </button>
            <button
              class="feed-tab"
              :class="{ active: eventTab === 'mine' }"
              @click="eventTab = 'mine'"
            >
              🎤 Мои события
              <span
                v-if="detectionStore.events.length > 0"
                class="tab-badge"
              >{{ detectionStore.events.length }}</span>
            </button>
          </div>

          <EventFeed
            v-if="eventTab === 'server'"
            :locating="locating"
            @focus="focusEvent"
            @focus-trajectory="focusTrajectory"
          />
          <MyEventsFeed
            v-else
            ref="myEvents"
            @retry-send="detection.retrySendEvent"
            @send-audio="onSendAudio"
          />
        </div>
      </div>
      <!-- End right column -->
    </div>
    <!-- End body -->

    <!-- On-screen debug log (floating 🐞 button + panel) -->
    <DebugLogPanel />

    <!-- First-run guided tour overlay (auto on first visit, reopenable via ❓ in header) -->
    <OnboardingTour
      :visible="onboardingVisible"
      :step="onboardingSteps[onboardingStep]"
      :step-index="onboardingStep"
      :total-steps="onboardingTotal"
      @next="onboardingNext"
      @back="onboardingBack"
      @skip="onboardingSkip"
    />
  </div>
</template>

<style scoped>
.live-radar {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  /* App shell never scrolls as a whole — each column manages its own scroll. Without this,
     Leaflet's zoom-proxy (an off-screen helper transformed thousands of px down) inflates the
     document height and produces a stray full-page vertical scrollbar. */
  overflow: hidden;
  background: #0f1420;
  color: #e6e9ef;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ── Responsive body: single column on phones, map | data split on desktop ── */
.body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.side-col {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
}

@media (max-width: 899px) {
  /* Settings open or manual placement armed: hide the event feed so the map stays visible. */
  .feed-section.feed-collapsed {
    display: none;
  }
  /* Manual placement: settings closed + feed hidden → let the map column fill the screen instead
     of the empty side column eating half the height via flex-grow. */
  .side-col.picking {
    flex: 0 0 auto;
  }
  /* Keep the map a stable, usable share of the screen even when the feed holds many events.
     The side column stops competing for free space (flex-grow 0) and the feed section is capped
     at half the viewport, so a long feed scrolls inside its own area instead of pushing the map
     down to 0 height. Map (flex-grow 1) always fills the remaining space. */
  .side-col {
    flex: 0 1 auto;
  }
  .feed-section {
    max-height: 50vh;
  }
}

@media (min-width: 900px) {
  .body {
    flex-direction: row;
    align-items: stretch;
  }
  /* Left: map column's share of the row (innards are styled by RadarMapView). flex-grow 1.5
     means it takes all the free space left after the (fixed-width, when resized) side column. */
  :deep(.map-col) {
    flex: 1.5 1 0;
    min-width: 0;
  }
  /* Right: data + settings. The column itself never scrolls — the tools panel and the
     event feed each manage their own scroll, so the "Рядом / Мои события" tabs stay pinned.
     Width is flex by default (1 1 0); once the user drags the splitter, an inline style pins it to
     a fixed px width (see sideColStyle). */
  .side-col {
    flex: 1 1 0;
    overflow: hidden;
    border-left: 1px solid #1c2638;
  }
  .side-col .tools-panel {
    max-height: 50vh;
    overflow-y: auto;
  }

  /* Draggable handle between the map and the data column. A thin invisible strip with a wider
     hit area (padding) and a visible grip line that brightens on hover/drag. */
  .splitter {
    flex: 0 0 auto;
    width: 1px;
    background: #1c2638;
    position: relative;
    cursor: col-resize;
    user-select: none;
    touch-action: none; /* let pointermove drive the drag instead of touch-scrolling */
  }
  .splitter::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -5px;
    right: -5px;
  }
  .splitter::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 2px;
    height: 28px;
    transform: translate(-50%, -50%);
    border-radius: 2px;
    background: #2a3754;
    transition: background 0.15s;
  }
  .splitter:hover::after,
  .splitter.dragging::after {
    background: #2aabee;
  }
}

/* The splitter only exists in the desktop row layout; hide it entirely on mobile (stacked). */
@media (max-width: 899px) {
  .splitter {
    display: none;
  }
}

.monitor-error {
  margin: 8px 12px 0;
  padding: 8px 12px;
  background: #2a1414;
  color: #ff8a8a;
  border-radius: 8px;
  font-size: 12px;
  flex-shrink: 0;
}

/* ── Event feed section ──
   A flex column pinned to the bottom of the side column: the tabs stay fixed at the top
   and the feed scrolls on its own, on both mobile and desktop. min-height:0 is required for a
   flex child to allow its content to shrink and scroll instead of overflowing the parent. */
.feed-section {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

/* ── Event feed tabs (own detections vs nearby server events) ── */
.feed-tabs {
  display: flex;
  gap: 8px;
  padding: 10px 12px 0;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.feed-tab {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid #243049;
  background: #161e2c;
  color: #aeb8cc;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.feed-tab.active {
  background: rgba(42, 171, 238, 0.16);
  border-color: #2aabee;
  color: #cde9fb;
}
.tab-badge {
  min-width: 18px;
  padding: 0 5px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: rgba(42, 171, 238, 0.25);
  color: #cde9fb;
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
</style>
